import React, { useState, useContext } from "react";
import styled from "styled-components";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Box,
} from "@material-ui/core";
import { Close, OpenInNew } from "@material-ui/icons";
import AppContext from "../../context/store";
import IyzicoLogoSvg from "../../assets/images/svg/iyizico-logo.svg";
import IyzicoSetupStep1 from "../../assets/images/iyzico-setup-step1.png";
import IyzicoSetupStep2 from "../../assets/images/iyzico-setup-step2.png";
import IyzicoSetupStep3 from "../../assets/images/iyzico-setup-step3.png";

const IyzicoSetupDialog = ({ open, onClose, onComplete }) => {
  const context = useContext(AppContext);
  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      // Son adımda, dialogu kapat
      handleClose();
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleClose = () => {
    setActiveStep(0);
    onClose();
  };

  const handleOpenIyzicoSite = () => {
    window.open("https://www.iyzico.com/isim-icin/hesap-olustur", "_blank");
  };

  const steps = [
    {
      label: "iyzico Hesabı Oluşturun ve Sanal Ödeme Çözümlerini Seçin",
      description: (
        <>
          <Typography variant="body2" paragraph>
            iyzico'da hesap oluşturun ve hesap tipini seçerken <strong>"Sanal Ödeme Çözümleri"</strong> seçeneğini işaretleyin.
          </Typography>
          <ButtonContainer style={{ marginBottom: 16 }}>
            <OpenLinkButton
              variant="contained"
              startIcon={<OpenInNew />}
              onClick={handleOpenIyzicoSite}
            >
              iyzico Hesabı Oluştur
            </OpenLinkButton>
          </ButtonContainer>
          <InfoList>
            <li><strong>Kurumsal Hesap</strong> seçin</li>
            <li>Firma bilgilerinizi, iletişim bilgilerinizi ve gerekli belgelerinizi doldurun</li>
            <li>
              <strong>Önemli:</strong> "Sanal Ödeme Çözümleri" seçeneğini işaretleyin
              <em> (Resimde kırmızı ile işaretli alan)</em>
            </li>
            <li>Sözleşmeyi onaylayın ve başvurunuzu tamamlayın</li>
          </InfoList>
          {IyzicoSetupStep1 && (
            <ImageContainer>
              <img src={IyzicoSetupStep1} alt="iyzico Hesap Oluşturma" onError={(e) => e.target.style.display = 'none'} />
            </ImageContainer>
          )}
          <Typography variant="body2" paragraph style={{ marginTop: 12, color: "#666", fontSize: "13px" }}>
            <strong>Not:</strong> Sanal POS, Pazaryeri ve Link Yöntemi gibi çözümler "Sanal Ödeme Çözümleri" seçeneğinde yer alır.
          </Typography>
        </>
      ),
    },
    {
      label: "API Anahtarlarını Alın ve Sisteme Ekleyin",
      description: (
        <>
          {IyzicoSetupStep2 && (
            <ImageContainer>
              <img src={IyzicoSetupStep2} alt="iyzico API Anahtarları" onError={(e) => e.target.style.display = 'none'} />
            </ImageContainer>
          )}
          <Typography variant="body2" paragraph>
            Hesabınızı oluşturduktan sonra iyzico paneline giriş yapıp API anahtarlarınızı alın ve sistemimize ekleyin.
          </Typography>
          <InfoList>
            <li>iyzico paneline giriş yapın</li>
            <li>Sol menüden <strong>"Ayarlar"</strong> sekmesine tıklayın ve <strong>"Firma Ayarları"</strong> bölümüne gidin</li>
            <li><strong>"API Anahtarları"</strong> kısmından <strong>API Anahtarı</strong> ve <strong>Güvenlik Anahtarı</strong> değerlerini kopyalayın</li>
          </InfoList>
          <hr />
          {IyzicoSetupStep3 && (
            <ImageContainer>
              <img src={IyzicoSetupStep3} alt="iyzico API Anahtarları" onError={(e) => e.target.style.display = 'none'} />
            </ImageContainer>
          )}
          <InfoList style={{ marginTop: 16 }}>
            <li>Iyzico Anahtarlarınızı aldıktan sonra sistemimizde sayfanın sağ üstünde bulunan profil menüsünden <strong>"Sistem Ayarları"</strong> sayfasına gidin</li>
            <li><strong>"İşletme Ayarları"</strong> bölümünde:
              <ul style={{ marginTop: 8 }}>
                <li>API Anahtarı → <strong>Iyzico API Anahtarı</strong> alanına yapıştırın</li>
                <li>Güvenlik Anahtarı → <strong>Iyzico Güvenlik Anahtarı</strong> alanına yapıştırın</li>
              </ul>
            </li>
            <li>Değişiklikleri kaydedin</li>
          </InfoList>
          <WarningBox>
            <Typography variant="body2">
              <strong>⚠️ Önemli:</strong> API anahtarlarınızı kimseyle paylaşmayın ve güvenli bir şekilde saklayın.
            </Typography>
          </WarningBox>
          <Typography variant="body2" paragraph style={{ marginTop: 16, fontWeight: 600, color: "#10b981" }}>
            ✓ Tüm adımları tamamladıktan sonra iyzico ile ödeme almaya başlayabilirsiniz!
          </Typography>
        </>
      ),
    },
  ];

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <StyledDialogTitle disableTypography>
        <LeftSection>
          <IyzicoLogo>
            <img src={IyzicoLogoSvg} alt="iyzico" />
          </IyzicoLogo>
          <TitleText>Sanal POS Kurulum Rehberi</TitleText>
        </LeftSection>
        <CloseButton onClick={handleClose} size="small">
          <Close />
        </CloseButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        <AlertBox>
          <Typography variant="body2">
            <strong>iyzico Kurulum Gerekli:</strong> iyzico ile ödeme almak için önce hesabınızı oluşturmalı ve API anahtarlarınızı sisteme eklemelisiniz. Aşağıdaki adımları takip ederek kurulumu tamamlayın.
          </Typography>
        </AlertBox>

        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>{step.label}</StepLabel>
              <StepContent>
                <StepContentBox>{step.description}</StepContentBox>
                <NavigationButtons>
                  <Button
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    variant="outlined"
                  >
                    Geri
                  </Button>
                  <NextButton
                    variant="contained"
                    onClick={handleNext}
                  >
                    {index === steps.length - 1 ? "Anladım" : "İleri"}
                  </NextButton>
                </NavigationButtons>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default IyzicoSetupDialog;

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    max-height: 90vh;
  }
`;

const StyledDialogTitle = styled(DialogTitle)`
  display: flex !important;
  flex-direction: row !important;
  justify-content: space-between !important;
  align-items: center !important;
  padding: 16px 20px !important;
  border-bottom: 1px solid #e0e0e0 !important;
  background: white !important;
  color: #333 !important;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const TitleText = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #333;
`;

const IyzicoLogo = styled.div`
  display: flex;
  align-items: center;

  img {
    height: 28px;
    width: auto;
  }
`;

const CloseButton = styled(IconButton)`
  color: #666 !important;
  padding: 6px !important;
  margin: 0 !important;

  &:hover {
    background: rgba(0, 0, 0, 0.05) !important;
  }

  svg {
    font-size: 20px;
  }
`;

const StyledDialogContent = styled(DialogContent)`
  padding: 20px !important;
  background: #fafafa;
`;

const AlertBox = styled.div`
  background: #fff3cd;
  border: 1px solid #ffc107;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 24px;

  .MuiTypography-root {
    color: #856404;
    font-size: 14px;
    line-height: 1.5;
  }
`;

const StepContentBox = styled(Box)`
  padding: 12px 0;
`;

const InfoList = styled.ul`
  margin: 12px 0;
  padding-left: 20px;
  color: #666;
  font-size: 14px;
  line-height: 1.8;

  li {
    margin-bottom: 8px;

    strong {
      color: #333;
      font-weight: 600;
    }
  }
`;

const ImageContainer = styled.div`
  margin: 16px 0;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const ButtonContainer = styled.div`
  margin-top: 16px;
`;

const OpenLinkButton = styled(Button)`
  background: #667eea !important;
  color: white !important;
  padding: 10px 20px !important;
  border-radius: 6px !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  text-transform: none !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;

  &:hover {
    background: #5568d3 !important;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3) !important;
  }

  .MuiButton-startIcon {
    margin-right: 8px;
  }
`;

const WarningBox = styled.div`
  background: #fff3e0;
  border-left: 3px solid #ff9800;
  padding: 12px;
  border-radius: 4px;
  margin-top: 16px;

  .MuiTypography-root {
    color: #e65100;
    font-size: 13px;
  }
`;

const NavigationButtons = styled(Box)`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  padding-top: 8px;
`;

const NextButton = styled(Button)`
  background: #667eea !important;
  color: white !important;
  padding: 8px 20px !important;
  border-radius: 6px !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  text-transform: none !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;

  &:hover {
    background: #5568d3 !important;
  }

  .MuiButton-startIcon {
    margin-right: 8px;
  }
`;
